import { allSuburbProfiles, getProfilesByCouncil } from './suburbProfilesData';

/**
 * Cluster 3: Council & Planning Explainers
 * - Per-suburb: "Development Approval Process in [Suburb]"
 * - Per-council: "[Council] DA Process Explained"
 * Total: 119 suburb + 12 council = 131 articles
 */

const img = (id) => `https://images.unsplash.com/photo-${id}?w=800&q=85`;

const images = [
  img('1600585154340-be6161a56a0c'), img('1605146769289-440113cc3d00'),
  img('1600607687939-ce8a6c25118c'), img('1564013799-e0cbc9f31999'),
  img('1600566753190-17f0baa2a6c0'), img('1600573472591-ee6981cf81f4'),
];

const councilData = {
  brisbane: { name: 'Brisbane City Council', scheme: 'Brisbane City Plan 2014', url: 'https://cityplan.brisbane.qld.gov.au', daTimeline: '6–12 months', codeTimeline: '30–50 business days' },
  'gold-coast': { name: 'City of Gold Coast', scheme: 'City of Gold Coast City Plan', url: 'https://cityplan.goldcoast.qld.gov.au', daTimeline: '6–12 months', codeTimeline: '25–40 business days' },
  logan: { name: 'Logan City Council', scheme: 'Logan Planning Scheme 2015', url: 'https://logan.qld.gov.au/planning-scheme', daTimeline: '4–10 months', codeTimeline: '25–35 business days' },
  'moreton-bay': { name: 'Moreton Bay Regional Council', scheme: 'Moreton Bay Regional Council Planning Scheme', url: 'https://www.moretonbay.qld.gov.au/planning-scheme', daTimeline: '4–10 months', codeTimeline: '25–40 business days' },
  redland: { name: 'Redland City Council', scheme: 'Redlands Planning Scheme', url: 'https://www.redland.qld.gov.au/planning', daTimeline: '4–10 months', codeTimeline: '25–35 business days' },
  ipswich: { name: 'Ipswich City Council', scheme: 'Ipswich Planning Scheme', url: 'https://www.ipswich.qld.gov.au/planning', daTimeline: '4–10 months', codeTimeline: '20–35 business days' },
  'sunshine-coast': { name: 'Sunshine Coast Council', scheme: 'Sunshine Coast Planning Scheme 2014', url: 'https://www.sunshinecoast.qld.gov.au/planning', daTimeline: '6–12 months', codeTimeline: '25–40 business days' },
  toowoomba: { name: 'Toowoomba Regional Council', scheme: 'Toowoomba Region Planning Scheme', url: 'https://www.tr.qld.gov.au/planning', daTimeline: '4–10 months', codeTimeline: '20–35 business days' },
  noosa: { name: 'Noosa Shire Council', scheme: 'Noosa Plan 2020', url: 'https://www.noosa.qld.gov.au/planning', daTimeline: '6–12 months', codeTimeline: '30–45 business days' },
  'scenic-rim': { name: 'Scenic Rim Regional Council', scheme: 'Scenic Rim Planning Scheme', url: 'https://www.scenicrim.qld.gov.au/planning', daTimeline: '3–8 months', codeTimeline: '20–30 business days' },
  'lockyer-valley': { name: 'Lockyer Valley Regional Council', scheme: 'Lockyer Valley Planning Scheme', url: 'https://www.lockyervalley.qld.gov.au/planning', daTimeline: '3–8 months', codeTimeline: '20–30 business days' },
  somerset: { name: 'Somerset Regional Council', scheme: 'Somerset Region Planning Scheme', url: 'https://www.somerset.qld.gov.au/planning', daTimeline: '3–8 months', codeTimeline: '20–30 business days' },
};

// Per-suburb articles
const createSuburbPlanningArticle = (profile, idx) => {
  const { name, slug, council, councilSlug, keyZoning, distanceFromCBD, infrastructure, developmentPotential } = profile;
  const cd = councilData[councilSlug] || { name: council, scheme: 'Local Planning Scheme', url: '#', daTimeline: '4–10 months', codeTimeline: '25–40 business days' };

  return {
    id: `${slug}-development-approval-process-guide`,
    category: 'Guide',
    title: `Development Approval in ${name}: What Property Owners Need to Know`,
    excerpt: `Understanding the development approval process in ${name} under ${cd.name}. A plain-English guide for property owners.`,
    date: '2026-04-09',
    readTime: '8 min read',
    image: images[idx % images.length],
    metaDescription: `Complete guide to development approval in ${name}. Understand the DA process, timelines, and what it means for your property under ${cd.name}.`,
    suburbs: [name],
    content: `
## Development Approval in ${name}: A Property Owner's Guide

If you own property in ${name}, understanding the development approval process is essential — whether you're planning to develop yourself or assessing your property's value to a developer buyer.

${name} falls under ${cd.name} and is governed by the ${cd.scheme}. This planning scheme determines what can be built, how tall, and how many dwellings are permitted on any given site.

## How Zoning Affects Your ${name} Property

Your property's zoning is the single biggest factor determining its development potential. In ${name}, the key zoning categories include:

**${keyZoning}**

Each zone has different rules for:
- Maximum building height
- Number of dwellings permitted
- Setbacks from boundaries
- Car parking requirements
- Open space and landscaping

You can check your property's specific zoning on the [${cd.scheme}](${cd.url}).

## Two Paths: Code Assessable vs. Impact Assessable

In Queensland, development applications fall into two main categories:

### Code Assessable
- The proposed development **complies** with the planning scheme
- Assessed against the specific codes in the scheme
- **No public notification required** — neighbours cannot formally object
- Faster process: typically ${cd.codeTimeline}
- Higher certainty of approval

### Impact Assessable
- The proposed development **doesn't fully comply** or is in a zone requiring impact assessment
- **Public notification period** — neighbours can submit objections
- Longer process: typically ${cd.daTimeline}
- Council has more discretion in decision-making
- Can be appealed to the Planning and Environment Court

For property sellers in ${name}, **code assessable sites are worth more** because they offer developers greater certainty and faster timelines.

## The DA Process Under ${cd.name}

| Stage | Timeframe | What Happens |
|---|---|---|
| Pre-lodgement meeting | 2–4 weeks | Developer meets council to discuss proposal |
| Application preparation | 4–8 weeks | Plans, reports, and documentation prepared |
| Lodgement | Day 1 | Application formally submitted to council |
| Information request | 2–6 weeks | Council may request additional information |
| Public notification (if impact) | 15–30 business days | Neighbours notified, submissions received |
| Assessment | 4–12 weeks | Council assesses against planning scheme |
| Decision | ${cd.daTimeline} total | Council approves, approves with conditions, or refuses |

## What This Means If You're Selling

Developers factor the approval process into their purchase price:

- **Code assessable sites** attract premium prices — developers have confidence in approval
- **Impact assessable sites** may still be valuable, but developers will factor in the risk and delay
- **Sites with existing approvals** command the highest prices — the uncertainty is removed

${developmentPotential ? `In ${name}: ${developmentPotential.slice(0, 200)}.` : ''}

## Common Planning Overlays in ${name}

Overlays can affect what's permitted on your site. Common overlays in ${name} include:

- **Flooding overlay** — may restrict development or require design responses
- **Character / heritage overlay** — may restrict demolition or require sympathetic design
- **Vegetation management** — may require tree retention or offsets
- **Infrastructure overlay** — may indicate future road widening or services
- **Bushfire overlay** — may require specific construction standards

Not all overlays prevent development — many simply require design responses that experienced developers accommodate routinely.

## Free Planning Assessment for ${name} Property Owners

ACRES provides complimentary planning assessments for ${name} property owners. We'll review your zoning, overlays, and constraints to explain exactly what your site's development potential is — in plain English.

**Request your free ${name} planning assessment today.**

## Frequently Asked Questions

**Q: How do I find out my ${name} property's zoning?**

A: You can check online at [${cd.scheme}](${cd.url}), or contact ACRES for a complimentary zoning review as part of our development site assessment.

**Q: Do I need development approval to sell to a developer?**

A: No. Most development sites are sold without an existing approval. The developer handles the DA process after purchase. However, sites with existing approvals typically achieve higher prices.

**Q: How long does a DA take in ${name}?**

A: Under ${cd.name}, code assessable applications typically take ${cd.codeTimeline}. Impact assessable applications take ${cd.daTimeline}. Timeframes vary based on complexity and information requests.
`,
    faq: [
      { q: `How do I find my ${name} property's zoning?`, a: `Check online at ${cd.scheme} or contact ACRES for a complimentary zoning review.` },
      { q: `Do I need DA approval before selling to a developer?`, a: `No. Most sites sell without existing approvals. However, approved sites achieve higher prices.` },
      { q: `How long does a DA take in ${name}?`, a: `Code assessable: ${cd.codeTimeline}. Impact assessable: ${cd.daTimeline}. Varies by complexity.` },
    ],
  };
};

// Per-council articles
const createCouncilPlanningArticle = (councilSlug) => {
  const cd = councilData[councilSlug];
  if (!cd) return null;
  const suburbs = getProfilesByCouncil(councilSlug);
  const suburbNames = suburbs.map(s => s.name).slice(0, 8).join(', ');

  return {
    id: `${councilSlug}-council-da-process-explained`,
    category: 'Guide',
    title: `${cd.name} DA Process Explained: A Complete Guide`,
    excerpt: `Everything property owners need to know about the development approval process under ${cd.name}. Timelines, costs, and what to expect.`,
    date: '2026-04-09',
    readTime: '10 min read',
    image: images[Object.keys(councilData).indexOf(councilSlug) % images.length],
    metaDescription: `Complete guide to the ${cd.name} development approval process. Timelines, fees, and what property owners in ${suburbNames} need to know.`,
    suburbs: suburbs.map(s => s.name),
    content: `
## ${cd.name}: Development Approval Process Explained

The ${cd.name} governs planning and development across suburbs including ${suburbNames}${suburbs.length > 8 ? ` and ${suburbs.length - 8} more` : ''}. If you own property in any of these areas, understanding the DA process helps you assess your property's development potential and value.

This guide covers everything you need to know about getting development approval under the ${cd.scheme}.

## How the ${cd.scheme} Works

The ${cd.scheme} is the legal document that controls all development within the council area. It specifies:

- **Zoning** for every parcel of land
- **Building height limits** by zone and precinct
- **Density provisions** (how many dwellings per hectare)
- **Infrastructure requirements** (roads, water, sewerage)
- **Environmental protections** and overlay provisions

You can access the full planning scheme at [${cd.scheme}](${cd.url}).

## Types of Development Assessment

### Accepted Development (No Approval Needed)
- Minor works that comply with all scheme requirements
- Examples: minor renovations, fences, sheds under size thresholds
- No application required

### Code Assessment
- Development that complies with scheme intent but needs assessment
- **No public notification** — third parties cannot object
- Assessment period: ${cd.codeTimeline}
- Higher certainty of approval
- Most common pathway for compliant development

### Impact Assessment
- Development that requires broader assessment
- **Public notification required** — 15–30 business days
- Submissions from public can be considered
- Assessment period: ${cd.daTimeline}
- Council has broader discretion
- Decision can be appealed to Planning and Environment Court

## Step-by-Step: The DA Process

### 1. Pre-Application Stage
- Review planning scheme zoning and overlays
- Engage town planner and architect
- Optional: pre-lodgement meeting with council ($500–$2,000)
- Prepare development plans and supporting reports

### 2. Lodgement
- Submit application with all required documents
- Pay application fees (typically $5,000–$30,000 depending on scale)
- Council assigns an assessment officer

### 3. Information Request Period
- Council reviews application completeness
- May issue an information request for additional details
- Applicant has 6 months to respond (can request extension)
- Common requests: traffic studies, stormwater reports, acoustic assessments

### 4. Public Notification (Impact Assessment Only)
- Applicant places notice on the site and in newspaper
- 15–30 business day submission period
- Council considers properly made submissions

### 5. Assessment and Decision
- Officer assesses against applicable codes
- Internal referral to engineering, environment, infrastructure departments
- Decision: approve, approve with conditions, or refuse
- Conditions typically cover landscaping, infrastructure contributions, design modifications

### 6. Post-Approval
- Compliance assessment for building works
- Operational works permits for earthworks, access, services
- Building approval (separate process through private certifier or council)

## What This Means for Property Sellers

Developers price development sites based on planning certainty:

| Scenario | Price Impact |
|---|---|
| Code assessable, no constraints | Highest value — low risk for developer |
| Code assessable, minor overlays | Strong value — manageable conditions |
| Impact assessable, supportable | Moderate discount — risk and time premium |
| Impact assessable, contentious | Significant discount — high uncertainty |
| Non-compliant proposal | Minimal premium — speculative only |

**Property owners don't need to understand every detail of the planning scheme.** What matters is knowing whether your site has genuine development potential — and that's exactly what ACRES assesses for free.

## Free Property Assessment

ACRES provides complimentary development site assessments for property owners across ${cd.name} suburbs including ${suburbNames}.

We review your zoning, overlays, constraints, and the competitive landscape to give you a clear picture of your property's development value.

**Request your free assessment today.**

## Frequently Asked Questions

**Q: How much does a DA cost under ${cd.name}?**

A: Application fees range from $5,000 to $30,000+ depending on the scale and type of development. The developer typically bears this cost when purchasing a site.

**Q: Can a DA be refused by ${cd.name}?**

A: Yes. However, code assessable applications that comply with the planning scheme have strong grounds for approval. Refusals can be appealed to the Planning and Environment Court.

**Q: Do I need a DA before selling my property to a developer?**

A: No. Most development sites sell without existing approvals. ACRES can advise whether obtaining approval first would increase your sale price.
`,
    faq: [
      { q: `How much does a DA cost under ${cd.name}?`, a: `Application fees range from $5,000 to $30,000+ depending on scale. The developer typically bears this cost.` },
      { q: `Can a DA be refused?`, a: `Yes, but code assessable applications that comply with the scheme have strong grounds for approval. Refusals can be appealed.` },
      { q: `Do I need a DA before selling to a developer?`, a: `No. Most sites sell without existing approvals. ACRES can advise if obtaining approval first would increase your price.` },
    ],
  };
};

export const suburbPlanningArticles = allSuburbProfiles.map((p, i) => createSuburbPlanningArticle(p, i));
export const councilPlanningArticles = Object.keys(councilData).map(createCouncilPlanningArticle).filter(Boolean);
export const allPlanningArticles = [...suburbPlanningArticles, ...councilPlanningArticles];
